import type { ApiAssistantStatus } from 'src/types/assistant';

import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { READ_TIMEOUT_MS } from 'src/utils/requestError';

import { QueryKeys } from './QueryKeys';

/**
 * Whether this installation has the storefront assistant (`GET /buyer/assistant/status`, public).
 * Asked once per visit — the answer changes only with a deploy — and only by the browser: the panel
 * that reads it renders behind `useHydrated()`, so the server never asks. `enabled: false` keeps
 * the panel off the page; so does an api that cannot be reached (nothing to show, no retry: the
 * catalog beside it has its own way of saying the api is down).
 */
export function useAssistantStatus(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: QueryKeys.assistantStatus(),
    queryFn: async (): Promise<ApiAssistantStatus> => {
      const response = await axios.get<ApiAssistantStatus>('/buyer/assistant/status', { timeout: READ_TIMEOUT_MS });
      return response.data;
    },
    enabled: options.enabled ?? true,
    retry: false,
    staleTime: Infinity,
  });
}
